// Type definitions
interface TemplateValue {
  key: string;
  responseHash: string;
  generatedTemplate: string;
  expiryTimestamp: number;
}

interface LookupResult {
  responseHash?: string;
  generatedTemplate?: string;
  expiryTimestamp?: number;
  [key: string]: any;
}

// IndexedDB constants
const DOM_TEMPLATE_DB_NAME = 'dom-template';
const DOM_TEMPLATE_STORE_NAME = 'template';
const DB_VERSION = 1;

// Utility: open IndexedDB
 function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DOM_TEMPLATE_DB_NAME, DB_VERSION);
    request.onerror = () => reject(new Error(request.error?.message ?? "IndexedDB open error"));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DOM_TEMPLATE_STORE_NAME)) {
        db.createObjectStore(DOM_TEMPLATE_STORE_NAME, { keyPath: 'key' });
      }
    };
  });
}

// Lookup template
 async function lookupTemplate(key: string, hashedValue?: string): Promise<TemplateValue | null> {
  try {
    const db = await openDB();
    return await new Promise<TemplateValue | null>((resolve, reject) => {
      const tx = db.transaction(DOM_TEMPLATE_STORE_NAME, 'readonly');
      const store = tx.objectStore(DOM_TEMPLATE_STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => {
        const item = req.result as TemplateValue | undefined;
        if (!item) return resolve(null);
        if (hashedValue && item.responseHash !== hashedValue) return resolve(null);
        if (isExpired(item.expiryTimestamp)) {
          deleteTemplate(key).catch(() => {});
          return resolve(null);
        }
        resolve(item);
      };
      req.onerror = () => reject(new Error(req.error?.message ?? "IndexedDB request error"));
    });
  } catch {
    return null;
  }
}

// Insert template
 async function insertTemplate(key: string, value: Omit<TemplateValue, "key">): Promise<void> {
  const db = await openDB();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(DOM_TEMPLATE_STORE_NAME, 'readwrite');
    const store = tx.objectStore(DOM_TEMPLATE_STORE_NAME);
    store.put({ key, ...value });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(new Error(tx.error?.message ?? "IndexedDB transaction error"));
  });
}

// Delete template
 async function deleteTemplate(key: string): Promise<void> {
  const db = await openDB();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(DOM_TEMPLATE_STORE_NAME, 'readwrite');
    const store = tx.objectStore(DOM_TEMPLATE_STORE_NAME);
    store.delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(new Error(tx.error?.message ?? "IndexedDB transaction error"));
  });
}

// Check expiry
 function isExpired(expiryTimestamp: number): boolean {
  return typeof expiryTimestamp === 'number' && Date.now() > expiryTimestamp;
}

// Generate DB key
 function generateKey(endpoint: string, querySelector?: string): string {
  let key = endpoint.replace(/[^\w]/g, '_');
  if (querySelector) {
    key += '__' + querySelector.replace(/[^\w]/g, '_');
  }
  return key;
}

// Incognito mode check
 async function isIncognitoMode(): Promise<boolean> {
  try {
    const db = indexedDB.open('incognito-test');
    return await new Promise<boolean>(resolve => {
      db.onerror = () => resolve(true);
      db.onsuccess = () => {
        db.result.close();
        indexedDB.deleteDatabase('incognito-test');
        resolve(false);
      };
    });
  } catch {
    return true;
  }
}

// Hash a response object
async function hashResponse(response: any): Promise<string> {
  try {
    const str = JSON.stringify(response);
    const encoded = new TextEncoder().encode(str);

    //  Copy into a new ArrayBuffer-backed Uint8Array
    const safe = new Uint8Array(encoded.length);
    safe.set(encoded);

    const hashBuffer = await crypto.subtle.digest('SHA-256', safe.buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return '';
  }
}


// Generate template HTML
 async function generateTemplate(data: any, querySelector?: string): Promise<string> {
  try {
    let template = `<div>${JSON.stringify(data)}</div>`;
    if (data.images && Array.isArray(data.images)) {
      for (const imgUrl of data.images) {
        try {
          const blob = await fetch(imgUrl).then(res => {
            if (!res.ok) throw new Error('Image fetch failed');
            return res.blob();
          });
          template += `<img src="${URL.createObjectURL(blob)}" />`;
        } catch {
          template += `<img alt="Image unavailable" />`;
        }
      }
    }
    return template;
  } catch {
    return '<div>Error generating template</div>';
  }
}

// Worker message handler
self.onmessage = async function(event) {
  try {
    const { type, payload } = event.data;

    switch (type) {
      case 'LOOKUP': {
        const { key, hashedValue } = payload;
        const value = await lookupTemplate(key, hashedValue);
        self.postMessage({ type: 'LOOKUP_RESULT', ...value });
        break;
      }
      case 'INSERT': {
        const { key, value } = payload;
        try {
          await insertTemplate(key, value);
          self.postMessage({ type: 'INSERT_RESULT', success: true });
        } catch (err) {
          self.postMessage({ type: 'INSERT_RESULT', success: false, error: String(err) });
        }
        break;
      }
      case 'DELETE': {
        const { key } = payload;
        try {
          await deleteTemplate(key);
          self.postMessage({ type: 'DELETE_RESULT', success: true });
        } catch (err) {
          self.postMessage({ type: 'DELETE_RESULT', success: false, error: String(err) });
        }
        break;
      }
      case 'CHECK_INCOGNITO': {
        const incognito = await isIncognitoMode();
        self.postMessage({ type: 'CHECK_INCOGNITO_RESULT', incognito });
        break;
      }
      case 'BACKGROUND_API_CALL': {
        const { endpoint, querySelector } = payload;
        try {
          const key = generateKey(endpoint, querySelector);
          const response = await fetch(endpoint).then(res => {
            if (!res.ok) throw new Error("Network error: " + res.status);
            return res.json();
          });
          const responseHash = await hashResponse(response);
          const cached = await lookupTemplate(key, responseHash) as LookupResult;
          if (cached && cached.responseHash === responseHash) {
            self.postMessage({ type: 'BACKGROUND_API_CALL_RESULT', updated: false });
            return;
          }
          const generatedTemplate = await generateTemplate(response, querySelector);
          const expiryTimestamp = Date.now() + 24 * 60 * 60 * 1000; // 1 day
          await insertTemplate(key, { responseHash, generatedTemplate, expiryTimestamp });
          self.postMessage({ type: 'BACKGROUND_API_CALL_RESULT', updated: true });
        } catch (err) {
          self.postMessage({
            type: 'BACKGROUND_API_CALL_RESULT',
            updated: false,
            error: String(err)
          });
        }
        break;
      }
      default:
        self.postMessage({ type: 'ERROR', error: 'Unknown message type' });
    }
  } catch (err) {
    self.postMessage({ type: 'ERROR', error: String(err) });
  }
};